/* eslint-disable brace-style */
import { Location } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { NotificationType } from '~core/enum';
import { AuthenticatedUser, Club, User } from '~core/models';
import {
  ClubService,
  DefaultNotificationService,
  NavigationService,
  StorageService,
  UserManagementService,
  UserService,
} from '~core/services';
import { ClubListTranslationsService, CustomerStatusTranslationsService } from '~shared/translations';

/**
 * Component responsible for user administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-overview-base',
  template: '',
  styleUrls: ['./user-overview-base.component.scss'],
})
export class UserOverviewBaseComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public submitted: boolean = false;
  public user: User;
  public authenticatedUser?: AuthenticatedUser;
  public listOfAllClubs: Array<Club> = [];
  public modalOpened: boolean = false;
  constructor(
    public notificationService: DefaultNotificationService,
    public navigationService: NavigationService,
    public userService: UserService,
    public userModalService: UserManagementService,
    public router: Router,
    public clubService: ClubService,
    public clubListTranslations: ClubListTranslationsService,
    public location: Location,
    public formBuilder: UntypedFormBuilder,
    public storage: StorageService,
    public route: ActivatedRoute,
    public customerStatusTranslations: CustomerStatusTranslationsService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => user !== null ? this.authenticatedUser = user : null);
  }

  /**
   * On init load the user fetched from the url param.
   */
  public ngOnInit(): void {
    this.route.params.pipe(untilDestroyed(this))
      .subscribe(params => {
        this.getUserById(params['id']);
      });
  }

  /**
   * On component destroy, close the modal.
   */
  public ngOnDestroy(): void {
    this.userModalService.toggleModal(false);
  }

  /**
   * Get the requested user.
   */
  public getUserById(userId: number): void {
    const calls: Array<undefined | Observable<any>> = [
      this.userService.getById(userId).pipe(untilDestroyed(this), catchError(error => of('errorFetchingUser')))
    ];
    if (this.authenticatedUser.isAdmin) {
      calls.push(this.clubService.fetchEnabledClubs(this.authenticatedUser).pipe(untilDestroyed(this), catchError(error => of('errorFetchingClubs'))));
    }

    forkJoin(calls).subscribe(([user, allClubs]) => {
      if (user === 'errorFetchingUser') {
        this.router.navigate(['/crm/user-management/users']);
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: 'Error occurred. Could not fetch this user.'
        });
      } else {
        this.user = new User(user, this.customerStatusTranslations);
      }
      if (this.authenticatedUser.isAdmin) {
        if (allClubs === 'errorFetchingClubs') {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.clubListTranslations.i18nErrorFetchingClubs
          });
        } else {
          this.listOfAllClubs = allClubs['clubs'];
        }
      }
      this.loading = false;
    });
  }

  /**
   * Update the state of this user.
   */
  public updateUser(user: User): void {
    this.user = user;
  }

  /**
   * Returns the user type after adding spaces to all capital letters.
   */
  public getUserType(type: any): string {
    return type.split(/(?=[A-Z])/).join(' ');
  }

}
